import { useState, useEffect } from "react";
import { useI18n } from "@/i18n";
import type { TypeUse } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        padding: "24px",
      }}
    >
      {children}
    </div>
  );
}

export function FormModal({
  item,
  onSave,
  onClose,
}: {
  item: TypeUse | null;
  onSave: (name: string) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState(item?.name ?? "");
  const [localError, setLocalError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 3) {
      setLocalError(t.typeUse.errorMinLength);
      return;
    }
    setLocalError("");
    onSave(name.trim());
  };

  return (
    <Overlay onClose={onClose}>
      <div
        style={{
          background: "#fff",
          borderRadius: "20px",
          padding: "32px",
          width: "100%",
          maxWidth: "420px",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
        onClick={e => e.stopPropagation()}
      >
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>
          {item ? t.typeUse.editTypeUse : t.typeUse.addTypeUse}
        </h2>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>
              {t.typeUse.nameLabel}
            </label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t.typeUse.namePlaceholder}
              autoFocus
              style={{
                fontFamily: typography.fontFamily,
                fontSize: typography.size.sm,
                color: "#111",
                background: "#fff",
                border: `1.5px solid ${localError ? colors.error : "#e5e5e5"}`,
                borderRadius: "10px",
                padding: "12px 16px",
                outline: "none",
                transition: `border-color ${transitions.fast}`,
                width: "100%",
                boxSizing: "border-box",
              }}
              onFocus={e => (e.currentTarget.style.borderColor = localError ? colors.error : "#111")}
              onBlur={e => (e.currentTarget.style.borderColor = localError ? colors.error : "#e5e5e5")}
            />
            {localError && (
              <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: colors.error }}>{localError}</span>
            )}
          </div>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                fontFamily: typography.fontFamily,
                fontSize: typography.size.sm,
                fontWeight: typography.weight.medium,
                color: "#666",
                background: "transparent",
                border: "1px solid #e0e0e0",
                borderRadius: "9px",
                padding: "10px 20px",
                cursor: "pointer",
              }}
            >
              {t.typeUse.cancel}
            </button>
            <button
              type="submit"
              style={{
                fontFamily: typography.fontFamily,
                fontSize: typography.size.sm,
                fontWeight: typography.weight.bold,
                color: "#fff",
                background: "#111",
                border: "none",
                borderRadius: "9px",
                padding: "10px 20px",
                cursor: "pointer",
                transition: `background ${transitions.fast}`,
              }}
              onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = "#333")}
              onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = "#111")}
            >
              {t.typeUse.save}
            </button>
          </div>
        </form>
      </div>
    </Overlay>
  );
}

export function DeleteModal({
  item,
  onConfirm,
  onClose,
}: {
  item: TypeUse;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useI18n();

  return (
    <Overlay onClose={onClose}>
      <div
        style={{
          background: "#fff",
          borderRadius: "20px",
          padding: "32px",
          width: "100%",
          maxWidth: "400px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          textAlign: "center",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            background: "rgba(239,68,68,0.1)",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
            color: colors.error,
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
          </svg>
        </div>
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>
          {t.typeUse.confirmDelete}
        </h2>
        <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888", margin: 0 }}>
          {t.typeUse.confirmDeleteDesc}
        </p>
        <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#111", margin: 0 }}>
          &ldquo;{item.name}&rdquo;
        </p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "8px" }}>
          <button
            onClick={onClose}
            style={{
              fontFamily: typography.fontFamily,
              fontSize: typography.size.sm,
              fontWeight: typography.weight.medium,
              color: "#666",
              background: "transparent",
              border: "1px solid #e0e0e0",
              borderRadius: "9px",
              padding: "10px 24px",
              cursor: "pointer",
            }}
          >
            {t.typeUse.cancel}
          </button>
          <button
            onClick={onConfirm}
            style={{
              fontFamily: typography.fontFamily,
              fontSize: typography.size.sm,
              fontWeight: typography.weight.bold,
              color: "#fff",
              background: colors.error,
              border: "none",
              borderRadius: "9px",
              padding: "10px 24px",
              cursor: "pointer",
              transition: `background ${transitions.fast}`,
            }}
            onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = "#dc2626")}
            onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = colors.error)}
          >
            {t.typeUse.confirmDeleteBtn}
          </button>
        </div>
      </div>
    </Overlay>
  );
}
